<template>
  <div class="d-flex flex-column justify-center align-center rel">
    <svg
      :viewBox="viewBox.box"
      class="max-width mx-auto"
    >
      <template v-for="(item, i) in list">
        <circle
          v-if="item.end - item.start === 360"
          :key="'circle' + i"
          :cx="size / 2"
          :cy="size / 2"
          :r="compact ? (size / 2) : (size / 2) - (shelf + 18)"
          :fill="item.color"
        />
        <path
          v-else
          :key="'path' + i"
          :d="item.d"
          :fill="item.color"
          stroke="white"
          stroke-width="2"
          fill-rule="evenodd"
          @click="compact ? clickPath(i) : null"
          @blur="compact && $vuetify.breakpoint.smAndDown ? sector = -1 : null"
          @mouseenter="compact && $vuetify.breakpoint.mdAndUp ? sector = i : null"
          @mouseleave="compact && $vuetify.breakpoint.mdAndUp ? sector = -1 : null"
          :class="{'pointer': compact}"
        />
        <template v-if="item.line && !compact">
          <line
            :key="'line' + i"
            :x1="item.line.start.x"
            :y1="item.line.start.y"
            :x2="item.line.end.x"
            :y2="item.line.end.y"
            stroke="#e6e6e6"
          />
          <line
            :key="'shelf' + i"
            :x1="item.line.end.x"
            :y1="item.line.end.y"
            :x2="item.line.end.x + (item.center > 180 ? -shelf : shelf)"
            :y2="item.line.end.y"
            stroke="#e6e6e6"
          />
          <text
            :key="'text' + i"
            :x="item.line.end.x + Number(item.center > 180 ? - shelf : 0) + (shelf / 2)"
            :y="item.line.end.y - 4"
            class="text"
            text-anchor="middle"
          >
            {{ showPercent ? item.percent + ' %' : item.text }}
          </text>
          <text
            v-if="showPercent"
            :key="'text_' + i"
            :x="item.line.end.x + Number(item.center > 180 ? - shelf : 0) + (shelf / 2)"
            :y="item.line.end.y + 14"
            class="text"
            text-anchor="middle"
          >
            {{ item.text }}
          </text>
        </template>
      </template>
      <circle
        :cx="size / 2"
        :cy="size / 2"
        :r="compact ? $vuetify.breakpoint.smAndDown ? ((size / 2) - 40) : ((size / 2) - 60) : (((size / 2) - (shelf + 18)) - 40)"
        fill="white"
      />
    </svg>
    <div class="abs d-flex justify-space-between align-center" v-if="compact && compactId != -1">
      <v-btn icon
             @click="showCompactInfo === 0 ? showCompactInfo = list.length -1 : showCompactInfo = showCompactInfo - 1">
        <v-icon size="30">mdi-chevron-left</v-icon>
      </v-btn>
      <div class="d-flex align-center gap-2">
        <v-icon :color="list[compactId].color" size="30">mdi-circle</v-icon>
        <div class="d-flex flex-column justify-center align-center">
          <div class="font-weight-bold">
            {{ list[compactId].text }}
          </div>
          <div class="font-weight-bold">
            {{ `${list[compactId].percent} %` }}
          </div>
        </div>
      </div>
      <v-btn icon
             @click="showCompactInfo === list.length - 1 ? showCompactInfo = 0 : showCompactInfo = showCompactInfo + 1">
        <v-icon size="30">mdi-chevron-right</v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
export default {
  name: "WidgetCircularDiagram",
  props: {
    items: {
      type: Array,
      default: () => {
        return []
      }
    },
    showPercent: {
      type: Boolean,
      default: true,
    },
    compact: Boolean,
  },
  data: () => ({
    extraHeightMinus: 0,
    extraHeightPlus: 0,
    largeData: false,
    sector: -1,
    showCompactInfo: 0,
  }),
  computed: {
    total() {
      return this.items.reduce((sum, a) => sum + Number(a.count), 0);
    },
    list() {
      let result = []
      let start = 0
      let temp = [[], [], [], []]
      for (let i in this.items) {
        let angle = (Number(this.items[i].count) * 360) / this.total
        let item = {
          start: start,
          end: start + angle,
          center: start + angle / 2,
          angle: angle,
          line: {},
          color: this.items[i].color,
          count: this.items[i].count,
          text: this.items[i].text,
          percent: Math.round((Number(this.items[i].count) * 10000) / this.total) / 100
        }
        start += angle
        let quoter = item.center < 90 ? 0 : item.center < 180 ? 1 : item.center < 270 ? 2 : 3
        temp[quoter].push(item)
        if (this.items[i].text && this.items[i].text.length > 9) {
          // eslint-disable-next-line vue/no-side-effects-in-computed-properties
          this.largeData = true
        }
      }
      for (let i in temp) {
        let quoter = i % 2 ? temp[i] : temp[i].reverse()
        for (let q in quoter) {
          quoter[q].indent = quoter[q - 1] && quoter[q - 1].angle < 60 ? quoter[q - 1].indent + 40 : 40
          let radius = this.compact ? this.size / 2 : (this.size / 2) - (this.shelf + 40)
          let center = this.size / 2
          let polar = {
            start: this.polarToCartesian(radius, quoter[q].end),
            end: this.polarToCartesian(radius, quoter[q].start),
            largeArcFlag: quoter[q].end - quoter[q].start <= 180 ? "0" : "1"
          }
          quoter[q].d = [
            "M", polar.start.x, polar.start.y,
            "A", radius, radius, 0, polar.largeArcFlag, 0, polar.end.x, polar.end.y,
            "L", center, center,
            "Z"
          ].join(" ")
          quoter[q].line.start = this.polarToCartesian(radius, quoter[q].center)
          quoter[q].line.end = this.polarToCartesian(radius + quoter[q].indent, quoter[q].center)
        }
        result = result.concat(i % 2 ? quoter : quoter.reverse())
      }
      return result
    },
    viewBox() {
      let radius = (this.size / 2) - (this.shelf + 18)
      if (!this.compact) {
        let temp = this.list.map(l => l.line).map(e => e.end)
        let x = temp.map(x => x.x)
        let y = temp.map(y => y.y)
        let xMin = Math.min(...x)
        xMin = xMin - this.shelf < 0 ? (xMin - this.shelf) * -1 : 0
        let xMax = Math.max(...x)
        xMax = xMax + this.shelf > this.size ? xMax + this.shelf - this.size : 0
        let yMin = Math.min(...y)
        yMin = (this.size / 2) - radius < yMin ? ((this.size / 2) - radius - 20) * -1 : (yMin - 20) * -1 //yMin - 20 < radius ? (yMin - 20) * -1 : -radius
        let yMax = Math.max(...y)
        yMax = (this.size / 2) + radius > yMax ? (this.size / 2) + radius + 20 - this.size : yMax + 20 - this.size //(yMax + 20) > radius ? (yMax + 20) - this.size : -radius
        let result = {
          't': yMin,
          'x': Math.max(xMin, xMax),
          'b': yMax,
        }
        let additional = this.size + result.t + result.b < 2 * radius ? 2 * radius : this.size + result.t + result.b
        result.box = Number(0 - result.x) + ' ' + Number(0 - result.t) + ' ' +
          Number(this.size + (2 * result.x)) + ' ' + Number(additional)
        return result
      }
      return {
        box: `0 0 ${this.size} ${this.size}`
      }
    },
    size() {
      return Number(this.circularSize) || this.$vuetify.breakpoint.xsOnly ? 300 : this.$vuetify.breakpoint.lgAndUp ? 500 : 450
    },
    shelf() {
      return this.$vuetify.breakpoint.smAndDown ? (this.largeData ? 120 : 85) : (this.largeData ? 150 : 120)
    },
    compactId() {
      return this.sector === -1 ? this.showCompactInfo : this.sector
    }
  },
  created() {
    if (this.list.length === 1) {
      this.sector = 0
    }
  },
  methods: {
    polarToCartesian(radius, angleInDegrees) {
      let center = this.size / 2
      let angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0
      return {
        x: center + (radius * Math.cos(angleInRadians)),
        y: center + (radius * Math.sin(angleInRadians))
      }
    },
    clickPath(i) {
      this.sector = i
      this.showCompactInfo = i
    }
  },
}
</script>

<style scoped lang="scss">
.rel {
  position: relative;
}

.abs {
  position: absolute;
  //width: calc(100% - 120px);
}

.pointer {
  cursor: pointer;
}

.text {
  font-size: 14px;
  fill: #277FAF;
  @media #{map-get($display-breakpoints, 'sm-and-down')} {
    font-size: 12px;
  }
}

svg {
  max-width: 80vw;
}

.max-width {
  max-width: 700px;
}

path {
  outline: none !important;
}
</style>
